
import { useState } from 'react';
import { toast } from 'sonner';
import { initiateStripeCheckout } from '@/services/subscription/stripeService';
import { supabase } from '@/lib/supabase';

export function useStripeCheckout() {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const refreshSession = async (): Promise<boolean> => {
    try {
      const { error } = await supabase.auth.refreshSession();
      if (error) {
        console.error("Erreur lors du rafraîchissement de la session:", error);
        return false;
      }
      return true;
    } catch (error) {
      console.error("Exception lors du rafraîchissement de la session:", error);
      return false;
    }
  };

  const handleCheckout = async (
    planId: string,
    organizationId: string,
    isYearly: boolean = false,
  ) => {
    if (!organizationId) {
      toast.error("Identifiant d'organisation non disponible");
      return;
    }

    setIsLoading(true);
    setHasError(false);
    
    try {
      // Vérifier que la session est valide avant de procéder
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData?.session?.access_token) {
        console.log("Session invalide ou expirée, tentative de refresh...");
        const refreshSuccess = await refreshSession();
        
        if (!refreshSuccess) {
          throw new Error("Session expirée. Veuillez vous reconnecter.");
        }
      }
      
      const returnUrl = window.location.href;
      console.log("URL de retour:", returnUrl);
      
      const response = await initiateStripeCheckout({
        planId,
        organizationId,
        isYearly,
        returnUrl
      });

      if (response.error) {
        if (response.error.includes("Session expirée")) {
          // Tenter un rafraîchissement automatique de la session
          const refreshSuccess = await refreshSession();
          if (refreshSuccess) {
            // Réessayer le checkout après rafraîchissement
            const newResponse = await initiateStripeCheckout({
              planId,
              organizationId,
              isYearly,
              returnUrl
            });
            
            if (newResponse.error) {
              setHasError(true);
              throw new Error(newResponse.error);
            }
            
            if (newResponse.url) {
              window.location.href = newResponse.url;
              return;
            }
          } else {
            setHasError(true);
            throw new Error("Impossible de rafraîchir votre session. Veuillez vous reconnecter.");
          }
        }
        
        setHasError(true);
        throw new Error(response.error);
      }

      if (response.url) {
        window.location.href = response.url;
      } else {
        setHasError(true);
        throw new Error("URL de paiement non reçue");
      }
    } catch (error: any) {
      console.error("Erreur lors de l'initialisation du paiement:", error);
      setHasError(true);
      toast.error(error.message || "Impossible d'initialiser le paiement");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    hasError,
    handleCheckout
  };
}
